/**
 * Present change order — the rep's side of the customer's page.
 *
 * The same two presses as an estimate, and deliberately so: Present freezes
 * what the customer will read and mints the link, and sending it is a second,
 * separate choice made three ways — email it from here if the rep has connected
 * Gmail, copy the link into whatever they already text from, or sign on this
 * device at the kitchen table. Jonathon, 2026-09-19, asked for exactly that:
 * "it needs to be just like the estimate presentation to where the rep has the
 * choice."
 *
 * Every unsigned change order on the estimate goes on one document (his third
 * decision the same day), so there is nothing to tick here — the list is shown,
 * not chosen from. Presenting again withdraws the live one and takes the same
 * lines, which is what stops a customer holding two links that disagree.
 */

import {
  buildChangeOrderDocument,
  changeOrdersToSend,
  formatMoney,
  jobAddress,
  type ChangeOrder,
  type Estimate,
  type Job,
  type LadderTotals,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote, Modal, Row } from "../../components/ui";
import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import {
  fetchChangeOrderTerms,
  fetchJobBalance,
  presentChangeOrder,
  type CompanySettingsRow,
} from "../../lib/queries";
import { ComposeEmail } from "../email/ComposeEmail";
import { ChangeOrderView } from "../present/ChangeOrderPage";

/** Today, from the rep's own device. Never built through `new Date(string)`. */
function today(): string {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

export function PresentChangeOrderModal({
  job,
  estimate,
  changeOrders,
  totals,
  company,
  companyLoading,
  repName,
  repPhone,
  gmailAddress,
  gmailLoading,
  gmailError,
  senders,
  ownSender,
  onClose,
  onPresented,
}: {
  job: Job;
  estimate: Estimate;
  changeOrders: ChangeOrder[];
  /** The ladder as it stands. The grand total already holds these lines. */
  totals: LadderTotals;
  company: CompanySettingsRow | null;
  /**
   * Whether company settings are still on their way. Present waits for them:
   * the name is frozen onto the customer's page, and core can only fall back
   * to the constant, not to whatever the settings row says.
   */
  companyLoading?: boolean;
  repName: string | null;
  repPhone: string | null;
  gmailAddress: string | null;
  gmailLoading?: boolean;
  gmailError?: string | null;
  senders?: Parameters<typeof ComposeEmail>[0]["senders"];
  ownSender?: Parameters<typeof ComposeEmail>[0]["ownSender"];
  onClose: () => void;
  onPresented: () => void | Promise<void>;
}) {
  const [link, setLink] = useState<string | null>(null);
  const [number, setNumber] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailing, setEmailing] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const terms = useAsync(fetchChangeOrderTerms, []);
  /**
   * What this customer has paid so far, frozen onto the page with everything
   * else.
   *
   * Jonathon, 2026-09-22: a change order has to reflect the balance they have
   * already paid. `fetchJobBalance` is the one answer to that question in the
   * whole app (ADR 0116), so the page a customer reads and the card the office
   * reads cannot disagree. While it is loading the document simply carries no
   * payment lines, which is also what a job nobody has paid on looks like —
   * and Present is disabled until it has arrived, so nothing is ever frozen
   * halfway.
   */
  const balance = useAsync(() => fetchJobBalance(job), [job.id]);
  const lines = changeOrdersToSend(changeOrders);

  /**
   * What the customer will read, built here and frozen at Present.
   *
   * Built on every render rather than inside the press, so the preview and the
   * document that goes out are the same object rather than two builds that
   * could differ.
   */
  const draft = buildChangeOrderDocument({
    // Numbered by the database at insert; until then it is the next one up, for
    // the preview only.
    number: number ?? 1,
    presentedAt: today(),
    company: {
      name: company?.name ?? "",
      phone: company?.phone ?? null,
      email: company?.email ?? null,
      address: company?.address ?? null,
    },
    customer: {
      name: job.contact?.full_name ?? "",
      address: jobAddress(job),
    },
    job: {
      // `jobs.job_number` is an integer; the snapshot keeps it as the string a
      // customer reads, so that nothing downstream does arithmetic on it.
      number: job.job_number == null ? null : String(job.job_number),
      address: jobAddress(job),
    },
    lines,
    grandTotal: totals.grandTotal,
    // `collected` and not `contract - outstanding`: the same number the
    // balance card shows as paid, straight from packages/core.
    paidToDate: balance.data?.collected ?? null,
    terms: terms.data ?? [],
    rep: repName ? { name: repName, phone: repPhone } : null,
  });

  const canEmail = Boolean(gmailAddress) && !gmailLoading;
  const whyNoEmail = gmailLoading
    ? t("Checking your email connection…")
    : gmailError
      ? gmailError
      : gmailAddress
        ? null
        : t("Connect your Google account in Tools to email from here.");

  async function present() {
    setBusy(true);
    setError(null);
    try {
      const made = await presentChangeOrder({
        jobId: job.id,
        estimateId: estimate.id,
        snapshot: draft,
      });
      setNumber(made.number);
      setLink(`${window.location.origin}/c/${made.token}`);
      await onPresented();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (previewing) {
    return (
      <Modal
        title={t("What the customer will see")}
        onClose={() => setPreviewing(false)}
      >
        <ChangeOrderView
          document={draft}
          signature={null}
          onSign={() => undefined}
        />
      </Modal>
    );
  }

  if (link) {
    return (
      <Modal title={t("Ready to approve")} onClose={onClose}>
        <p>
          {t(
            "Change order {n} is locked and the link is live for 30 days. Editing these lines from now on will not change what the customer sees — to change their copy, present it again, which replaces this one.",
            { n: number ?? "" },
          )}
        </p>
        <Row label={t("Link")}>
          <input readOnly value={link} onFocus={(e) => e.target.select()} />
        </Row>
        <div className="mact">
          <button
            type="button"
            onClick={() => void navigator.clipboard?.writeText(link)}
          >
            {t("Copy link")}
          </button>
          <button
            type="button"
            disabled={!canEmail}
            title={whyNoEmail ?? undefined}
            onClick={() => setEmailing(true)}
          >
            {t("Email the link")}
          </button>
          <button
            type="button"
            className="primary"
            onClick={() => window.location.assign(link)}
          >
            {t("Sign on this device")}
          </button>
        </div>
        <p className="n">
          {canEmail
            ? t(
                "Email it below, or paste the link wherever you already talk to this customer. Texting is still by hand.",
              )
            : t(
                "{why} Until then, paste the link wherever you already talk to this customer.",
                { why: whyNoEmail ?? "" },
              )}
        </p>
        {emailing && (
          <ComposeEmail
            title={t("Email change order {n} to {name}", {
              n: number ?? "",
              name: job.contact?.full_name ?? t("the customer"),
            })}
            subtitle={t("They can read it and approve it on their phone.")}
            to={job.contact?.email ?? ""}
            subject={`Change order for ${jobAddress(job)} — ${draft.company.name}`}
            body={[
              `Hi ${(job.contact?.full_name ?? "").split(" ")[0]},`,
              "",
              "Something came up on your job that was not in the agreement you signed. Here it is, with what it costs:",
              "",
              link,
              "",
              `It adds ${formatMoney(draft.changeTotal)}, which brings your job to ${formatMoney(draft.contractAfter)}.`,
              ...(draft.paidToDate != null && draft.paidToDate > 0
                ? [
                    `You have paid ${formatMoney(draft.paidToDate)} so far, so the balance would be ${formatMoney(draft.balanceAfter ?? (0 as never))}.`,
                  ]
                : []),
              "",
              "You can read it and approve it from your phone. Give me a call if you would rather talk it through first.",
              "",
              "Thanks,",
              repName ?? "",
            ].join("\n")}
            contact={job.contact}
            contactId={job.contact_id}
            jobId={job.id}
            connectedAs={gmailAddress ?? null}
            senders={senders}
            ownSender={ownSender ?? null}
            onClose={() => setEmailing(false)}
            onSent={onClose}
          />
        )}
      </Modal>
    );
  }

  return (
    <Modal title={t("Present change order")} onClose={onClose}>
      <p className="n">
        {job.job_number ? `${t("Job {n}", { n: job.job_number })} · ` : ""}
        {job.contact?.full_name ?? jobAddress(job)}
      </p>

      {lines.length === 0 ? (
        <p>
          {t(
            "Every change order on this estimate has been signed. Add one to send another.",
          )}
        </p>
      ) : (
        <>
          <p>{t("Going on this document:")}</p>
          <table className="etable">
            <tbody>
              {lines.map((line) => (
                <tr key={line.id}>
                  <td>{line.description || "Roofing work"}</td>
                  <td className="num">
                    {formatMoney(
                      draft.lines.find((l) => l.id === line.id)?.amount ??
                        (0 as never),
                    )}
                  </td>
                </tr>
              ))}
              <tr>
                <td>
                  <strong>{t("New contract total")}</strong>
                </td>
                <td className="num">
                  <strong>{formatMoney(draft.contractAfter)}</strong>
                </td>
              </tr>
              {/* What the customer will be shown about money already paid,
                  so the rep sees the same three lines they will. */}
              {draft.paidToDate != null && draft.paidToDate > 0 ? (
                <>
                  <tr>
                    <td>{t("Paid so far")}</td>
                    <td className="num">− {formatMoney(draft.paidToDate)}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>{t("Balance after this")}</strong>
                    </td>
                    <td className="num">
                      <strong>
                        {formatMoney(draft.balanceAfter ?? (0 as never))}
                      </strong>
                    </td>
                  </tr>
                </>
              ) : null}
            </tbody>
          </table>

          {draft.terms.length === 0 ? (
            <p className="warn">
              {t(
                'No change order terms are written yet, so this will go out with nothing under "Read the terms". Tools → Change Order Terms.',
              )}
            </p>
          ) : null}

          <ErrorNote error={error} />

          <div className="mact">
            <button type="button" onClick={() => setPreviewing(true)}>
              {t("Preview")}
            </button>
            <button
              type="button"
              className="primary"
              // Not until the balance has arrived. Presenting freezes the
              // page (ADR 0032), and a document frozen a second early is one
              // that tells a customer who has paid half their roof nothing
              // about it, for the 30 days the link lives.
              disabled={busy || balance.loading || companyLoading}
              onClick={() => void present()}
            >
              {busy || companyLoading ? t("One moment…") : t("Present")}
            </button>
          </div>
          <p className="n">
            {t(
              "Presenting locks what the customer reads and creates their link. It does not send anything — you choose how it goes out next.",
            )}
          </p>
        </>
      )}
    </Modal>
  );
}
